<template>
  <div data-testid="occupation-marital-status-info-wrapper">
    <InputFieldText
      data-testid="occupation-info"
      :label-name="t('OCCUPATION')"
      placeholder="--"
      v-model="formData.data.occupation"
      :required="true"
      @update:modelValue="validate"
    />
    <Dropdown
      :dropdown-Label="t('MARITAL_STATUS')"
      :options="maritalStatusDropdown"
      v-model="formData.data.maritalStatus"
      @update:modelValue="handleMaritalStatusChange"
      data-testid="marital-status"
      placeholder="--"
    />
    <InputFieldText
      data-testid="spouse-full-name"
      v-if="formData.data.maritalStatus === 'MARRIED'"
      :label-name="t('SPOUSE_FULL_NAME')"
      placeholder="--"
      v-model="formData.data.spouseFullName"
      :required="true"
      @update:modelValue="validate"
    />
    <div class="mb-2">
      {{ t("ACTING_ON_BEHALF_OF_A_THIRD_PARTY") }}
    </div>
    <ToggleButton
      :left-label="t('YES')"
      :right-label="t('NO')"
      :default-active-option="0"
      @update-switch="handleThirdPartyToggle"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import InputFieldText from "@/apps/merchant3/shared-components/input-fields/InputFieldText.vue";
import Dropdown from "@/apps/merchant3/shared-components/ui/Dropdown.vue";
import ToggleButton from "@/apps/merchant3/shared-components/common/ToggleButton.vue";
import { IOccupationMaritalStatusInfo } from "@/apps/merchant3/types/customer-registration/customer-contact-info";
import { useStore } from "vuex";

const store = useStore();
const { t } = useI18n();
const emit = defineEmits<{
  (e: "update:data", IOccupationMaritalStatusInfo): void;
}>();

const formData = ref<IOccupationMaritalStatusInfo>({
  isValid: false,
  data: {
    cartTag: store.state.carts?.cartTag ?? "",
    occupation: "",
    maritalStatus: "",
    spouseFullName: "",
    actingOnBehalfOfAThirdParty: "",
  },
});

const maritalStatusDropdown = [
  {
    name: t("SINGLE"),
    code: "SINGLE",
  },
  {
    name: t("MARRIED"),
    code: "MARRIED",
  },
  {
    name: t("DIVORCED"),
    code: "DIVORCED",
  },
  {
    name: t("WIDOWED"),
    code: "WIDOWED",
  },
  {
    name: t("SEPARATED"),
    code: "SEPARATED",
  },
  {
    name: t("CIVIL_UNION"),
    code: "CIVIL_UNION",
  },
  {
    name: t("OTHER"),
    code: "OTHER",
  },
  {
    name: t("PREFER_NOT_TO_SAY"),
    code: "PREFER_NOT_TO_SAY",
  },
];

const validate = () => {
  formData.value.isValid = !!(
    formData.value.data.occupation &&
    formData.value.data.maritalStatus &&
    (formData.value.data.maritalStatus !== "MARRIED" ||
      formData.value.data.spouseFullName) &&
    formData.value.data.actingOnBehalfOfAThirdParty
  );

  emit("update:data", formData.value);
};

const handleMaritalStatusChange = (value: { name: string; code: string }) => {
  formData.value.data.maritalStatus = value.code;
  if (value.code !== "MARRIED") {
    formData.value.data.spouseFullName = "";
  }
  validate();
};

const handleThirdPartyToggle = (value: number) => {
  formData.value.data.actingOnBehalfOfAThirdParty = value === 1 ? "yes" : "no";
  validate();
};

watch(formData, validate, { deep: true });
</script>
